import React, { useEffect, useState } from "react";
import { Table, Form, Row, Col, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import "../../../../style/addrestoration.css";
import "../../../../style/addmaterial.css";
import { apiAxios } from "../../../../utils/apiAxios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface CancellationReason {
  cancellationReasonID: number;
  cancellationReasonName: string;
  isActive: boolean;
  createdDate?: string;
  updatedDate?: string;
}

const CancellationReason: React.FC = () => {
  const [reasonName, setReasonName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [reasons, setReasons] = useState<CancellationReason[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editActive, setEditActive] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const fetchCancellationReasons = async () => {
    try {
      const res = await apiAxios.get(
        `${API_BASE}/CancellationReason/GetAll`
      );
      const data = res.data;

      if (data?.isSuccess && Array.isArray(data.singleData)) {
        setReasons([...data.singleData].reverse());
      } else {
        setReasons([]);
      }
    } catch (err) {
      console.error("Error fetching cancellation reasons:", err);
      setReasons([]);
    }
  };

  useEffect(() => {
    fetchCancellationReasons();
  }, []);

  const handleAdd = async () => {
    const trimmedName = reasonName.trim();

    if (!trimmedName) {
      toast.warning("Please enter cancellation reason!");
      return;
    }

    const isDuplicate = reasons.some(
      (r) =>
        r.cancellationReasonName.toLowerCase() ===
        trimmedName.toLowerCase()
    );

    if (isDuplicate) {
      toast.warning("Cancellation reason already exists!");
      return;
    }

    try {
      const res = await apiAxios.post(
        `${API_BASE}/CancellationReason/Create`,
        {
          cancellationReasonName: trimmedName,
          isActive,
        }
      );

      const result = res.data;

      if (result.isSuccess) {
        setReasons([result.singleData, ...reasons]);
        toast.success("Added successfully!");
        setReasonName("");
        setIsActive(true);
      } else {
        toast.error(result.status || "Add failed");
      }
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  const handleEdit = (
    id: number,
    name: string,
    active: boolean
  ) => {
    setEditId(id);
    setEditValue(name);
    setEditActive(active);
  };

  const handleUpdate = async () => {
    if (!editValue.trim() || editId === null) return;

    const isDuplicate = reasons.some(
      (r) =>
        r.cancellationReasonName.toLowerCase() ===
          editValue.toLowerCase() &&
        r.cancellationReasonID !== editId
    );
    if (isDuplicate) {
      toast.warning("Cancellation reason already exists!");
      return;
    }

    try {
      const res = await apiAxios.put(
        `${API_BASE}/CancellationReason/Update`,
        {
          cancellationReasonID: editId,
          cancellationReasonName: editValue,
          isActive: editActive,
        }
      );

      const result = res.data;

      if (result.isSuccess) {
        setReasons(
          reasons.map((r) =>
            r.cancellationReasonID === editId
              ? {
                  ...r,
                  cancellationReasonName: editValue,
                  isActive: editActive,
                }
              : r
          )
        );

        toast.success("Updated successfully!");
        setEditId(null);
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const handleCancel = () => {
    setEditId(null);
    setEditValue("");
    setEditActive(true);
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (deleteId === null) return;

    try {
      const res = await apiAxios.delete(
        `${API_BASE}/CancellationReason/Delete/${deleteId}`
      );

      const result = res.data;

      if (result.isSuccess) {
        setReasons(
          reasons.filter(
            (r) => r.cancellationReasonID !== deleteId
          )
        );

        toast.success("Deleted successfully!");
      }
    } catch {
      toast.error("Delete failed");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="mt-1">
      <h4 className="mb-4">Add Cancellation Reason</h4>
      <div className="premium-card mt-3">
        <label className="form-label">
          Add Cancellation Reason
        </label>

        <Row className="mb-2">
          <Col md={12}>
            <Form.Control
              type="text"
              placeholder="Enter Cancellation Reason"
              value={reasonName}
              onChange={(e) => setReasonName(e.target.value)}
            />
          </Col>
        </Row>

        <Row>
          <Col className="text-start mt-2">
            <button
              type="submit"
              className="btn py-2 px-3 btn-primary"
              onClick={handleAdd}
            >
              Add Reason
            </button>
          </Col>
        </Row>
      </div>

      <div className="premium-card mt-5">
        <h5 className="mb-3">Cancellation Reason List</h5>

        <Table
          bordered
          hover
          responsive
          className="align-middle addshade-table2 mb-0 custom-material-table"
        >
          <thead className="table-light">
            <tr>
              <th style={{ width: "80px" }}>ID</th>
              <th>Reason Name</th>
              <th style={{ width: "100px" }}>Status</th>
              <th style={{ width: "250px" }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {reasons.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted">
                  No cancellation reasons added yet
                </td>
              </tr>
            ) : (
              reasons.map((item) => (
                <tr key={item.cancellationReasonID}>
                  <td>{item.cancellationReasonID}</td>

                  <td>
                    {editId === item.cancellationReasonID ? (
                      <Form.Control
                        type="text"
                        value={editValue}
                        onChange={(e) =>
                          setEditValue(e.target.value)
                        }
                      />
                    ) : (
                      <span className="font-weight500">
                        {item.cancellationReasonName}
                      </span>
                    )}
                  </td>

                  <td>
                    {editId === item.cancellationReasonID ? (
                      <Form.Check
                        type="checkbox"
                        checked={editActive}
                        onChange={(e) =>
                          setEditActive(e.target.checked)
                        }
                      />
                    ) : (
                      <span
                        className={`badge ${
                          item.isActive
                            ? "bg-success"
                            : "bg-danger"
                        }`}
                      >
                        {item.isActive
                          ? "Active"
                          : "Inactive"}
                      </span>
                    )}
                  </td>

                  <td>
                    {editId === item.cancellationReasonID ? (
                      <div className="d-flex justify-content-center gap-2">
                        <div
                          className="action-btn delete custom-edit-icon"
                          onClick={handleUpdate}
                        >
                          <i
                            className="bi bi-check-lg"
                            title="Update"
                          ></i>
                        </div>

                        <div
                          className="action-btn delete custom-edit-icon"
                          onClick={handleCancel}
                        >
                          <i
                            className="bi bi-x-lg"
                            title="Cancel"
                          ></i>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-center gap-2">
                        <div
                          className="action-btn delete custom-edit-icon"
                          onClick={() =>
                            handleEdit(
                              item.cancellationReasonID,
                              item.cancellationReasonName,
                              item.isActive
                            )
                          }
                        >
                          <i
                            className="bi bi-pencil-square"
                            title="Edit"
                          ></i>
                        </div>

                        <div
                          className="action-btn delete custom-edit-icon"
                          onClick={() =>
                            confirmDelete(
                              item.cancellationReasonID
                            )
                          }
                        >
                          <i
                            className="bi bi-trash"
                            title="Delete"
                          ></i>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        dialogClassName="custom-top-modal"
      >
        <Modal.Body className="text-center">
          <p className="fs-6">
            Are you sure you want to delete this cancellation
            reason?
          </p>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <button
            type="button"
            className="btn py-2 px-3 btn-primary"
            onClick={handleDelete}
          >
            Yes
          </button>

          <button
            type="button"
            className="btn py-2 px-3 btn-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            No
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CancellationReason;